import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import type { BatchScale } from "@/lib/recipes/staged-method";
import type { CookingSession } from "@/types/cooking-session";
import { CompactCard } from "./CompactCard";
import { RecipeMethodPanel, type MethodLayout } from "./RecipeMethodPanel";
import { SegmentedControl } from "./SegmentedControl";
import { SessionTopBar } from "./SessionTopBar";

interface SpotlightViewProps {
  sessions: CookingSession[];
  spotlightId: number | null;
  onSpotlightChange: (id: number) => void;
  secondsOf: (session: CookingSession) => number;
  scaleOf: (id: number) => BatchScale;
  onScaleChange: (id: number, scale: BatchScale) => void;
  onError: (message: string) => void;
}

/**
 * Spotlight, "RedZone" (YC-26): one recipe large with its full ingredients and
 * procedure, the rest stacked as compact cards.
 */
export function SpotlightView({ sessions, spotlightId, onSpotlightChange, secondsOf, scaleOf, onScaleChange, onError }: SpotlightViewProps) {
  const { t } = useLingui();
  const [layout, setLayout] = useState<MethodLayout>("staged");
  const lit = sessions.find((s) => s.id === spotlightId) ?? sessions[0];
  if (!lit) return null;
  const others = sessions.filter((s) => s.id !== lit.id);

  return (
    <div data-testid="spotlight-view" className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <section aria-label={t`Spotlight`} className="flex min-w-0 flex-col gap-4">
        <SessionTopBar session={lit} seconds={secondsOf(lit)} onError={onError} />
        <div className="flex justify-end">
          <SegmentedControl<MethodLayout>
            aria-label={t`Recipe layout`}
            value={layout}
            onChange={setLayout}
            options={[
              { value: "staged", label: t`Staged` },
              { value: "simple", label: t`Simple` },
            ]}
          />
        </div>
        <RecipeMethodPanel
          recipeId={lit.recipeId}
          layout={layout}
          scale={scaleOf(lit.id)}
          onScaleChange={(scale) => onScaleChange(lit.id, scale)}
        />
      </section>
      <aside aria-label={t`Other recipes`} className="flex flex-col gap-3 lg:max-h-[calc(100dvh-12rem)] lg:overflow-y-auto">
        {others.map((session) => (
          <CompactCard
            key={session.id}
            session={session}
            seconds={secondsOf(session)}
            scale={scaleOf(session.id)}
            onFocus={() => onSpotlightChange(session.id)}
            onError={onError}
          />
        ))}
      </aside>
    </div>
  );
}
